import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AppProvider, useAppContext } from './context/AppContext';
import { TbkConnectionProvider } from './context/TbkConnectionContext';
import { XSignConnectionProvider } from './context/XSignConnectionContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import TokenLogin from './components/auth/TokenLogin';
import MainLayout from './components/layout/MainLayout';
import CategorySelectionScreen from './components/categories/CategorySelectionScreen';
import ProductList from './components/products/ProductList';
import PaymentScreen from './components/checkout/PaymentScreen';
import PaymentMethodScreen from './components/checkout/PaymentMethodScreen';
import OperateCardScreen from './components/checkout/OperateCardScreen';
import ThankYouScreen from './components/checkout/ThankYouScreen';
import WelcomeScreen from './components/welcome/WelcomeScreen';
import useInactivityTimer from './hooks/useInactivityTimer';
import connectionService from './services/ConnectionService';
import './index.css';

const queryClient = new QueryClient();

const AppRoutes = ({ isAuthenticated, setIsAuthenticated }) => {
  const { clearSessionData } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);

  const { isInactive, resetTimer, stopTimer, startTimer } = useInactivityTimer(20000);

  const shouldShowWelcome = isAuthenticated &&
    isInactive &&
    !showWelcome &&
    location.pathname !== '/login' &&
    location.pathname !== '/payment' &&
    location.pathname !== '/payment-method' &&
    location.pathname !== '/operate-card' &&
    location.pathname !== '/thank-you';

  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login') {
      if (location.pathname === '/payment' ||
        location.pathname === '/payment-method' ||
        location.pathname === '/operate-card' ||
        location.pathname === '/thank-you') {
        stopTimer();
      } else {
        startTimer();
      }
    } else {
      stopTimer();
    }
  }, [isAuthenticated, location.pathname, startTimer, stopTimer]);

  useEffect(() => {
    if (shouldShowWelcome) {
      console.log('Mostrando pantalla de bienvenida por inactividad');
      setShowWelcome(true);
    }
  }, [shouldShowWelcome]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    resetTimer();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearSessionData();
    setShowWelcome(false);
    stopTimer();
  };

  const handleWelcomeStart = () => {
    console.log('Usuario inició desde pantalla de bienvenida');
    setShowWelcome(false);
    resetTimer();
    navigate('/');
  };

  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  if (showWelcome) {
    return <WelcomeScreen onStart={handleWelcomeStart} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<TokenLogin onLogin={handleLogin} />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <CategorySelectionScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:categoryId"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <ProductList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <PaymentScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-method"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <PaymentMethodScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/operate-card"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <OperateCardScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/thank-you"
        element={
          <ProtectedRoute>
            <MainLayout onLogout={handleLogout}>
              <ThankYouScreen />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isOnline, setIsOnline] = useState(connectionService.getConnectionStatus());

  useEffect(() => {
    console.log('App: Inicializando ConnectionService...');

    const unsubscribe = connectionService.subscribe((status) => {
      console.log('App: Cambio de conexión detectado:', status ? 'Conectado' : 'Desconectado');
      setIsOnline(status);
    });

    return () => {
      console.log('App: Limpiando suscripción a ConnectionService');
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log('App: Verificando conectividad inicial...');
    connectionService.forceCheck()
      .then((status) => {
        console.log('App: Verificación inicial completada, estado:', status ? 'Conectado' : 'Desconectado');
      })
      .catch((error) => {
        console.error('App: Error en verificación inicial:', error);
      });
  }, []);

  useEffect(() => {
    if (isOnline) {
      console.log('App: Sistema conectado - Las ventas se enviarán a la API intermedia en tiempo real');
    } else {
      console.log('App: Sistema desconectado - La API intermedia se encargará de sincronizar automáticamente cuando se restablezca la conexión');
    }
  }, [isOnline]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <TbkConnectionProvider>
          <XSignConnectionProvider>
            <CartProvider>
              <Router>
                <AppRoutes
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                />
              </Router>
            </CartProvider>
          </XSignConnectionProvider>
        </TbkConnectionProvider>
      </AppProvider>
    </QueryClientProvider>
  );
}

export default App;